import { CustomLink } from '@/app/_components/CustomLink';

import type { MedianIncomeYear, MedianIncomeYearBundle } from '../constants';
import { MEDIAN_INCOME_BY_YEAR } from '../constants';

interface PolicyCardsProps {
  year: MedianIncomeYear;
  /** 사용자 비율에서 매핑된 가장 가까운 구간(%). null 이면 empty state. */
  activeTier: number | null;
}

const CARD_CLASS =
  'flex items-start gap-12 rounded-small border-1 border-Gray-100 bg-Gray-25 p-16';
const CARD_HOVER_CLASS = 'transition-colors hover:border-B-300 hover:bg-B-25';

/**
 * ⑤ 정책 카드 — `activeTier` 에 해당하는 정책 목록을 카드 한 줄에 하나씩 노출.
 * - `url` 이 있으면 카드 전체가 CustomLink — 클릭 시 정책 상세로 이동.
 * - `url` 이 없으면 일반 article(클릭 비활성).
 */
export const PolicyCards = ({ year, activeTier }: PolicyCardsProps) => {
  //* `as const` 로 narrow 된 키를 동적 문자열로 인덱싱하기 위해 인터페이스 타입으로 widening.
  const { policiesByTier }: MedianIncomeYearBundle =
    MEDIAN_INCOME_BY_YEAR[year];
  const policies =
    activeTier === null ? [] : (policiesByTier[String(activeTier)] ?? []);

  return (
    <section className="flex flex-col gap-12">
      <h2 className="font-700 text-Gray-900 typo-h5 mobile-tablet:px-16 mobile-tablet:typo-h6">
        {activeTier === null ? (
          '중위소득 구간 정책'
        ) : (
          <>
            중위소득 <span className="text-B-600">{activeTier}%</span> 구간 주요
            정책
          </>
        )}
      </h2>

      {activeTier === null ? (
        <p className="rounded-small bg-Gray-50 px-16 py-24 text-center text-Gray-600 typo-body4 mobile-tablet:mx-16">
          위 표에서 구간을 선택하거나 계산기를 사용해 보세요.
        </p>
      ) : policies.length === 0 ? (
        <p className="rounded-small bg-Gray-50 px-16 py-24 text-center text-Gray-600 typo-body4 mobile-tablet:mx-16">
          해당 구간의 정책이 없습니다.
        </p>
      ) : (
        <div className="flex flex-col gap-12 mobile-tablet:px-16">
          {policies.map((p) => {
            const body = (
              <div className="flex min-w-0 flex-1 flex-col gap-8">
                <div className="font-700 text-Gray-900 typo-body3">
                  {p.name}
                </div>
                <p className="text-Gray-700 typo-body4">{p.summary}</p>
                <dl className="flex flex-col gap-4 text-Gray-600 typo-caption1">
                  <div className="flex gap-8">
                    <dt className="shrink-0 font-600">대상</dt>
                    <dd>{p.target}</dd>
                  </div>
                  <div className="flex gap-8">
                    <dt className="shrink-0 font-600">금액</dt>
                    <dd>{p.amount}</dd>
                  </div>
                </dl>
              </div>
            );

            return p.url ? (
              <CustomLink
                key={p.slug}
                href={p.url}
                className={`${CARD_CLASS} ${CARD_HOVER_CLASS}`}
              >
                {body}
                <span
                  aria-hidden="true"
                  className="shrink-0 self-center text-Gray-400 typo-body3"
                >
                  →
                </span>
              </CustomLink>
            ) : (
              <article key={p.slug} className={CARD_CLASS}>
                {body}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
};
